(() => {
  const VALA = window.VALA;

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('[data-section-id]');
      this.variants = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '[]');
      this.selects = Array.from(this.querySelectorAll('select'));
      this.selects.forEach((select) => select.addEventListener('change', () => this.onVariantChange()));
      this.updateOptionAvailability();
    }

    get selectedOptions() {
      return this.selects.map((select) => select.value);
    }

    onVariantChange() {
      const selectedOptions = this.selectedOptions;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selectedOptions[index])
      );
      this.currentVariant = variant || null;
      this.updateOptionAvailability();
      this.updateProductState();
    }

    updateOptionAvailability() {
      const selected = this.selectedOptions;
      this.selects.forEach((select, index) => {
        Array.from(select.options).forEach((option) => {
          const available = this.variants.some((variant) => {
            if (!variant.available || variant.options[index] !== option.value) return false;
            return variant.options.slice(0, index).every((value, priorIndex) => value === selected[priorIndex]);
          });
          option.disabled = !available;
        });
      });
    }

    updateProductState() {
      const variant = this.currentVariant;
      const sectionId = this.dataset.section;
      const addButtons = this.section.querySelectorAll('[data-add-to-cart-text]');
      const submitButtons = this.section.querySelectorAll('.product-form__submit');
      const stickyButton = document.querySelector('[data-sticky-add]');

      if (!variant) {
        submitButtons.forEach((button) => { button.disabled = true; });
        if (stickyButton) stickyButton.disabled = true;
        addButtons.forEach((element) => { element.textContent = VALA.strings.unavailable; });
        this.updateAvailability(false, VALA.strings.unavailable);
        return;
      }

      this.section.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant.id;
      });
      submitButtons.forEach((button) => { button.disabled = !variant.available; });
      if (stickyButton) stickyButton.disabled = !variant.available;
      addButtons.forEach((element) => {
        element.textContent = variant.available ? VALA.strings.addToCart : VALA.strings.soldOut;
      });

      this.updatePrice(sectionId, variant);
      this.updateAvailability(
        variant.available,
        variant.available ? (variant.inStock ? VALA.strings.inStock : VALA.strings.available) : VALA.strings.soldOut
      );
      const sku = this.section.querySelector('[data-product-sku]');
      if (sku) sku.textContent = variant.sku || '—';
      if (variant.featuredMediaId) {
        this.section.querySelector('media-gallery')?.select(variant.featuredMediaId);
      }
      if (variant.url) window.history.replaceState({}, '', variant.url);
      const availabilityText = variant.available
        ? (variant.inStock ? VALA.strings.inStock : VALA.strings.available)
        : VALA.strings.soldOut;
      VALA.announce(`${variant.price}. ${availabilityText}`);
    }

    updatePrice(sectionId, variant) {
      const container = document.getElementById(`Price-${sectionId}`);
      if (container) {
        container.textContent = '';
        const price = document.createElement('div');
        price.className = variant.compareAtPrice ? 'price price--on-sale' : 'price';
        const current = document.createElement('span');
        current.className = variant.compareAtPrice ? 'price-item price-item--sale' : 'price-item price-item--regular';
        current.textContent = variant.price;
        price.append(current);
        if (variant.compareAtPrice) {
          const compare = document.createElement('s');
          compare.className = 'price-item price-item--compare';
          compare.textContent = variant.compareAtPrice;
          price.append(compare);
        }
        if (variant.unitPrice && variant.unitReferenceUnit) {
          const unit = document.createElement('small');
          unit.className = 'unit-price';
          const referenceValue = variant.unitReferenceValue && Number(variant.unitReferenceValue) !== 1
            ? String(variant.unitReferenceValue) + ' '
            : '';
          unit.textContent = variant.unitPrice + ' / ' + referenceValue + variant.unitReferenceUnit;
          price.append(unit);
        }
        container.append(price);
      }
      const stickyPrice = document.getElementById(`StickyPrice-${sectionId}`);
      if (stickyPrice) stickyPrice.textContent = variant.price;
    }

    updateAvailability(available, text) {
      const container = this.section.querySelector('[id^="Availability-"]');
      if (container) {
        container.classList.toggle('availability--available', available);
        container.classList.toggle('is-available', available);
        container.classList.toggle('availability--unavailable', !available);
        container.classList.toggle('is-unavailable', !available);
        const label = container.querySelector('[data-product-availability]');
        if (label) label.textContent = text;
      }
      const sticky = document.querySelector('[data-sticky-availability]');
      if (sticky) {
        sticky.textContent = text;
        sticky.classList.toggle('availability--available', available);
        sticky.classList.toggle('availability--unavailable', !available);
      }
    }
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-product-form]');
      this.error = this.querySelector('[data-product-form-error]');
      this.button = this.querySelector('[type="submit"]');
      if (!this.form) return;
      this.form.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.disabled = false;
      });
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      document.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
        if (!this.button.disabled) this.form.requestSubmit(this.button);
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.button.disabled || this.button.classList.contains('is-loading')) return;
      this.setLoading(true);
      this.showError('');
      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      try {
        const response = await fetch(VALA.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || VALA.strings.error);
        if (VALA.cartType === 'drawer' && VALA.cartDrawer) {
          await VALA.cartDrawer.renderSection(data.sections?.['cart-drawer']);
          VALA.cartDrawer.open(this.button);
        } else {
          window.location.assign(VALA.routes.cart);
        }
        await VALA.cartDrawer?.updateCount();
      } catch (error) {
        this.showError(error.message || VALA.strings.error);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-disabled', String(loading));
    }

    showError(message) {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message;
    }
  }

  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
})();
